import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ErrorBoundary from '~/components/ErrorBoundary';
import sanitizeHtml from 'sanitize-html';
import {
  Button,
  ButtonVariant,
  Icon,
  IconName,
  MultiSelect,
  Token,
  useSnackBar,
} from '@usealloy/component-library';
import log from '~/helpers/logging';

import Input from '~/components/inputs/Input';
import { ajaxFetch } from '~/helpers/api';
import apiCalls from '~/helpers/apiCalls';
import { usePermissions, useFetchedUserContext } from '~/helpers/hooks';
import { routes } from '~/helpers/routes';
import NotesList from '~/notes/NotesList';
import NotesModal from '~/notes/NotesModal';

const { entities: { documents: { renameDocument } } } = apiCalls;

const UploadedDocumentPropTypes = {
  addNoteCallback: PropTypes.func,
  doc: PropTypes.shape({
    content_type: PropTypes.string,
    url: PropTypes.string,
    review_url: PropTypes.string,
    approval_timestamp: PropTypes.number,
    approval_outcome: PropTypes.bool,
    alternative_outcome: PropTypes.string,
    filename: PropTypes.string,
    document_token: PropTypes.string,
    approval_agent_first_name: PropTypes.string,
    approval_agent_last_name: PropTypes.string,
    review_timestamp: PropTypes.string,
    document_type: PropTypes.string,
    formatted_timestamp: PropTypes.string,
    notes: PropTypes.arrayOf(PropTypes.shape({})),
    document_id: PropTypes.number,
    entity_token: PropTypes.string,
    evaluation_tokens: PropTypes.arrayOf(PropTypes.string),
    extension: PropTypes.string,
    timestamp: PropTypes.string,
  }).isRequired,
  entityToken: PropTypes.string,
  review: PropTypes.func,
  reviewCallback: PropTypes.func,
  changeStatus: PropTypes.bool,
  onDocumentNameChange: PropTypes.func,
  hasPermissionToEdit: PropTypes.bool,
};

const UploadedDocumentDefaultProps = {
  addNoteCallback: null,
  review: null,
  reviewCallback: () => {},
  changeStatus: true,
  hasPermissionToEdit: true,
};

const UploadedDocument = ({
  addNoteCallback,
  doc,
  entityToken,
  review: _review,
  reviewCallback,
  changeStatus,
  onDocumentNameChange,
  hasPermissionToEdit,
}) => {
  const context = useFetchedUserContext();
  const { typhonDomain, feature_flags: featureFlags } = context;
  const addSnackBar = useSnackBar();

  const [permissions] = usePermissions();
  const canViewDoc = permissions['READ:Documents:null'];
  const [documentContent, setDocumentContent] = useState(null);
  const [docFileName, setDocumentName] = useState(doc.filename);
  useEffect(() => {
    if (doc.content_type && doc.content_type.includes('html') && !documentContent) {
      ajaxFetch(doc.url, { json: false }).then(data => { setDocumentContent(data); });
    }
  }, [doc, documentContent]);

  // last element of the array is the token of the last evaluation run:
  const latestEvaluationToken = doc.evaluation_tokens
    && doc.evaluation_tokens[doc.evaluation_tokens.length - 1];

  const handleReviewDocument = useCallback(selected => {
    const { value } = selected;
    const url = doc?.review_url;
    if (value !== 'unreviewed') {
      const confirmStr = `Are you sure you want to ${value} this document?`;
      if (confirm(confirmStr)) {
        const isApproved = value !== 'resubmit' ? value === 'approve' : undefined;
        const resubmitOutcome = value === 'resubmit' ? 'resubmit' : undefined;
        const review = _review
          ? _review({ isApproved, resubmitOutcome })
          : ajaxFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              approved: isApproved,
              alternative_outcome: resubmitOutcome,
            }),
          });
        review.then(data => {
          if (data.result === 'success') window.location.reload();
          else {
            reviewCallback(data);
            addSnackBar({ title: data.message, type: 'error' });
          }
        });
      }
    }
  }, [doc, _review, addSnackBar, reviewCallback]);


  const renderDocumentStatus = () => {
    try {
      if (!changeStatus) {
        return null;
      }
      if (doc.approval_timestamp === null) {
        return permissions['UPDATE:Documents:approved'] ? (
          <MultiSelect
            isMulti={false}
            label="Status"
            placeholder="Unreviewed"
            options={[
              { label: 'Approve', value: 'approve' },
              { label: 'Deny', value: 'deny' },
              ...(
                featureFlags['document-re-request']
                  ? [{ label: 'Resubmit Document', value: 'resubmit' }]
                  : []
              ),
            ]}
            onChange={handleReviewDocument}
            selectedOptions={null}
          />
        ) : 'Unreviewed';
      }

      if (doc.alternative_outcome === 'resubmit') {
        return <span className="bold red">Resubmit Document</span>;
      }

      if (doc.approval_outcome) {
        return <span className="bold green">Approved</span>;
      }

      return <span className="bold red">Denied</span>;
    } catch (error) {
      log('error', 'Error in renderDocumentStatus', { url: window.location.pathname }, error);
      return <span className="bold red">Unable to display document status</span>;
    }
  };

  const register = () => ({
    type: 'text',
    value: docFileName,
    onChange: event => setDocumentName(event.target.value),
  });

  const updateDocName = async () => {
    const confirmStr = `Are you sure you want to rename this document to ${docFileName}?`;
    if (!confirm(confirmStr)) {
      return;
    }

    const success = await renameDocument({
      documentToken: doc.document_token,
      entityToken: doc.entity_token,
      name: docFileName,
      typhonDomain,
    });

    if (success) {
      addSnackBar({ title: `Document name changed to ${docFileName}`, type: 'success' });
      if (onDocumentNameChange) onDocumentNameChange();
    }
  };

  const filenameWithExtension = useMemo(() => {
    const extension = `.${doc.extension}`;
    const filenameIncludesExtension = doc.filename.slice(-1 * extension.length) === extension;
    return filenameIncludesExtension
      ? doc.filename
      : `${doc.filename}${extension}`;
  }, [doc]);

  const notes = (doc?.notes ?? []).map(note => ({
    ...note,
    created_at: moment.tz(note.created_at, 'UTC').tz(moment.tz.guess()).toString(),
  }));

  return (
    <div className="document pad-16-24 header-16">
      <div className="row header-48">
        <div className={`col flex ${canViewDoc ? 'flex-col' : ''} start align-start header-space`}>
          <div className="flex start tiny-header-space header-16">
            <h3 className="header-8">{docFileName}</h3>
            <Token displayContent={doc.document_token} />
          </div>

          {canViewDoc && (
            <>
              <Input
                width={324}
                label="Rename Document"
                name="filename"
                error="Document name required"
                required
                register={register}
              />
              <Button
                disabled={!hasPermissionToEdit}
                onClick={updateDocName}
                variant={ButtonVariant.primary}
              >
                Update Document Name
              </Button>
              <br />
            </>
          )}
          <ErrorBoundary
            withResetChildren
            loggingCallback={error => {
              log('error', 'Error in Uploaded Document', { url: window.location.pathname }, error);
            }}
          >
            <div className="bold header-16 small-text">
              {renderDocumentStatus()}
            </div>
          </ErrorBoundary>

          {doc.approval_timestamp !== null ? [
            <div key={0} className="header-16 small-text">
              <p className="bold">Reviewed by </p>
              <p className="grey">
                {`${doc.approval_agent_first_name} ${doc.approval_agent_last_name}`}
              </p>
            </div>,
            <div key={1} className="header-16 small-text">
              <p className="bold">Reviewed at </p>
              <p className="grey">{moment.tz(doc.approval_timestamp, 'UTC').tz(moment.tz.guess()).format('MMM DD, YYYY, h:mm A')}</p>
            </div>,
          ] : false}

          <div className="header-16 small-text">
            <p className="bold">Type</p>
            <p className="grey">{doc.document_type}</p>
          </div>

          <div className="header-16 small-text">
            <p className="bold">Uploaded</p>
            <p className="grey">{moment.tz(doc.timestamp, 'UTC').tz(moment.tz.guess()).format('MMM DD, YYYY, h:mm A')}</p>
          </div>

          {entityToken && latestEvaluationToken && (
            <div className="header-16 small-text">
              <p className="bold">Evaluation</p>
              <p>
                <a
                  className="secondary inline"
                  href={routes.entities.evaluation.detail({
                    entityToken, evaluationToken: latestEvaluationToken,
                  })}
                  rel="noreferrer"
                  target="_blank"
                >
                  {latestEvaluationToken}
                </a>
              </p>
            </div>
          )}

          {canViewDoc ? (
            <div className="header-16 small-text">
              <p className="bold">Download</p>
              <p>
                <a
                  href={doc.url}
                  target="_blank"
                  className="secondary inline"
                  download={filenameWithExtension}
                  rel="noreferrer"
                >
                  {`${filenameWithExtension} `}
                  <Icon icon={IconName.Download} />
                </a>
              </p>
            </div>
          ) : false}
        </div>

        {canViewDoc ? (
          <div className="col-12 col-md-8 document-iframe">
            {(() => {
              if (doc.content_type && doc.content_type.includes('html')) {
                return documentContent
                  // eslint-disable-next-line react/no-danger
                  && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(documentContent) }} />;
              }

              if (!doc.content_type) {
                return (
                  <p>
                    Document could not be displayed. Please download in order to view.
                  </p>
                );
              }

              if (doc.content_type.split('/')[0] === 'image' && doc.extension !== 'pdf') {
                return (
                  <img
                    src={doc.url}
                    alt={doc.filename}
                    width="100%"
                    height="auto"
                  />
                );
              }

              return (
                <>
                  <a href={doc.url} target="_blank" rel="noreferrer">
                    full screen
                  </a>
                  <iframe src={doc.url} width="100%" height="600px" />
                </>
              );
            })()}
          </div>
        ) : false}
      </div>

      <div className="row header-24">
        <div className="col small-text">
          <div className="flex start header-16"><h4>Notes</h4></div>

          <NotesList
            notes={notes}
            rowKey="document_id"
            rowId={doc.document_id}
          />
        </div>
      </div>

      {hasPermissionToEdit && (
        <div className="row header-24">
          <div className="col flex start">
            <NotesModal
              trigger={<Button variant={ButtonVariant.primary}>Add Note</Button>}
              addingNoteTo={{
                table: 'Documents',
                key: 'document_id',
                row_id: doc.document_id,
                token: doc.document_token,
                title: doc.filename,
                entity_token: doc.entity_token,
              }}
              getState={addNoteCallback}
            />
          </div>
        </div>
      )}
    </div>
  );
};
UploadedDocument.propTypes = UploadedDocumentPropTypes;
UploadedDocument.defaultProps = UploadedDocumentDefaultProps;


export default UploadedDocument;
